<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppButton, AppLinkButton } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import { useGlobalSuggestionSwitchStore } from '../../stores/globalSuggestionSwitchStore';

import type { GlobalSuggestionSwitchOption } from './globalSuggestionTypeSwitch.model';
import { GlobalSuggestionSwitchType } from './globalSuggestionTypeSwitch.model';

interface GlobalSuggestionTypeSwitchEmits {
  (eventName: 'low-price-calendar-click'): void;
}

const emit = defineEmits<GlobalSuggestionTypeSwitchEmits>();

const globalSuggestionSwitchStore = useGlobalSuggestionSwitchStore();
const browserStore = useBrowserStore();

function shouldHighlightLabel(option: GlobalSuggestionSwitchOption): boolean {
  return (
    option.type === GlobalSuggestionSwitchType.LowestPrice &&
    browserStore.isLargerThan1199px
  );
}

function shouldHighlightPrice(option: GlobalSuggestionSwitchOption): boolean {
  return (
    option.type === GlobalSuggestionSwitchType.LowestPrice &&
    !browserStore.isLargerThan1199px
  );
}

const isLowPriceCalendarButtonExperiment = computedAsync(
  () => isExperimentAsync('low_price_calendar_button'),
  false
);

function updateSuggestion(suggestionType: GlobalSuggestionSwitchType): void {
  if (globalSuggestionSwitchStore.currentSwitchType !== suggestionType) {
    globalSuggestionSwitchStore.updateSwitchType(suggestionType);
  }
}

function onLowPriceCalendarClick(): void {
  emit('low-price-calendar-click');
}
</script>

<template>
  <div class="global-suggestion-type-switch">
    <AppButton
      v-for="option in globalSuggestionSwitchStore.suggestionOptions"
      :key="option.type"
      :class="{
        'is-selected': option.isSelected,
      }"
      class="global-suggestion-type-switch-button"
      type="secondary"
      size="medium"
      state="enabled"
      :no-border="true"
      @click="updateSuggestion(option.type)"
    >
      <div
        class="label"
        :class="{
          'is-premade-trip': shouldHighlightLabel(option),
        }"
      >
        {{ option.text }}
        <span
          v-if="option.price"
          class="price"
          :class="{
            'is-premade-trip': shouldHighlightPrice(option),
          }"
        >
          {{ option.price }}
        </span>
      </div>
    </AppButton>

    <div
      v-if="
        browserStore.isLargerThan599px && isLowPriceCalendarButtonExperiment
      "
      class="low-price-calendar-button"
    >
      <AppLinkButton
        :text="$t('planner.experiments.low_price_calendar_button.label')"
        size="s"
        :bold="true"
        :underline="false"
        left-icon="lowPriceCalendar"
        icon-size="18"
        @click="onLowPriceCalendarClick"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.global-suggestion-type-switch {
  --static-button-width-mobile: 183px;
  --static-button-width: 240px;

  position: relative;
  overflow: hidden;
  width: 100%;
  display: flex;
  padding: var(--spacing-8) var(--spacing-12) 0 var(--spacing-12);
  background-color: var(--color-white);
  justify-content: center;
  @include for-more-or-equal-than-1200-px {
    padding-top: 0;
  }

  .global-suggestion-type-switch-button {
    border-radius: 0;
    padding: var(--spacing-4) var(--spacing-16) var(--spacing-8)
      var(--spacing-16);
    width: var(--static-button-width);
    @include for-less-or-equal-than-1199-px {
      width: var(--static-button-width-mobile);
    }
  }
  .is-selected {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: var(--spacing-4);
      background-color: var(--color-brand-violet);
      border-radius: var(--border-radius-4);
    }
  }
  .is-premade-trip {
    background-color: var(--color-brand-summer);
    padding: var(--spacing-4) var(--spacing-8);
    border-radius: var(--border-radius-4);
  }
  .label {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-4);

    @include for-less-or-equal-than-1199-px {
      flex-direction: column;
      gap: 0;
    }
    @include font-label-s-light;

    .price {
      @include font-label-m-bold;
    }
  }

  .low-price-calendar-button {
    display: flex;
    align-items: center;
    padding: var(--spacing-4) var(--spacing-16) var(--spacing-8)
      var(--spacing-16);

    @include on-hover {
      background-color: var(--color-neutral-20);
    }
  }
}
</style>
